import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import {
  Search, MapPin, Home, ArrowRight,
  CheckCircle, Star, Shield, Phone, Calendar
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const AREAS = [
  { name: "Houston", desc: "Downtown, Midtown, Montrose, Heights", searches: "12,000+ monthly searches" },
  { name: "Katy", desc: "Cinco Ranch, Grand Lakes, Mason Creek", searches: "8,500+ monthly searches" },
  { name: "Sugar Land", desc: "Town Square, Imperial, New Territory", searches: "6,200+ monthly searches" },
  { name: "The Woodlands", desc: "Waterway, Research Forest, Panther Creek", searches: "7,800+ monthly searches" },
  { name: "Conroe", desc: "Lake Conroe, Downtown, Grand Central", searches: "4,100+ monthly searches" },
];

const PRICE_RANGES = [
  "Under $1,000", "$1,000 - $1,500", "$1,500 - $2,000", "$2,000 - $2,500", "$2,500+"
];

export default function HoustonApartmentFinder() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", area: "", budget: "", bedrooms: "" });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const budgetMap = {
        "Under $1,000": [0, 1000],
        "$1,000 - $1,500": [1000, 1500],
        "$1,500 - $2,000": [1500, 2000],
        "$2,000 - $2,500": [2000, 2500],
        "$2,500+": [2500, 5000],
      };
      const [min, max] = budgetMap[form.budget] || [0, 5000];
      const response = await axios.post(`${API}/instant-search`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        budget_min: min,
        budget_max: max,
        bedrooms: form.bedrooms,
        preferred_area: form.area,
      });
      navigate("/instant-results", { state: { results: response.data, searchData: form } });
    } catch {
      toast({ title: "Something went wrong", variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="seo-landing-page">
      {/* Hero */}
      <section className="relative bg-[#0A2342] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage:"radial-gradient(circle at 20% 50%, #DE6E4B 0%, transparent 50%), radial-gradient(circle at 80% 80%, #8A9A5B 0%, transparent 50%)"}} />
        </div>
        <div className="container mx-auto px-4 py-20 md:py-28 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}>
              <span className="inline-flex items-center px-4 py-2 bg-[#DE6E4B] rounded-full text-sm font-medium mb-6" data-testid="seo-hero-badge">
                <Star className="w-4 h-4 mr-2" /> Houston's #1 Free Apartment Locator
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Playfair_Display'] leading-tight" data-testid="seo-hero-title">
                Find Your Perfect Houston Apartment
              </h1>
              <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                Search thousands of apartments across Houston, Katy, Sugar Land, The Woodlands & Conroe. 
                My locator service is <strong className="text-white">100% free</strong> — apartment communities pay my fee.
              </p>
            </motion.div>
            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.2}} className="flex flex-wrap justify-center gap-4">
              <a href="#finder" className="inline-flex items-center px-8 py-4 bg-[#DE6E4B] text-white font-bold rounded-xl hover:bg-[#c5593d] transition-colors text-lg" data-testid="seo-hero-cta">
                <Search className="w-5 h-5 mr-2" /> Find Apartments Now
              </a>
              <a href="/book" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-8 py-4 bg-white/10 border border-white/30 text-white font-bold rounded-xl hover:bg-white/20 transition-colors text-lg" data-testid="seo-hero-book">
                <Calendar className="w-5 h-5 mr-2" /> Book Free Consultation
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-[#E8E8E8] py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-sm text-[#0A2342]">
            {[
              { icon: CheckCircle, text: "100% Free Service" },
              { icon: Home, text: "500+ Properties" },
              { icon: Star, text: "5-Star Reviews" },
              { icon: Shield, text: "Licensed & Insured" },
              { icon: Phone, text: "(936) 224-1484" },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2 font-medium">
                <item.icon className="w-4 h-4 text-[#8A9A5B]" />
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apartment Finder Form */}
      <section id="finder" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A2342] mb-4 font-['Playfair_Display']" data-testid="seo-finder-title">
                Instant Apartment Finder
              </h2>
              <p className="text-slate-600 text-lg">Tell me what you need and I'll find your matches in seconds</p>
            </div>

            <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 shadow-lg" data-testid="seo-finder-form">
              <form onSubmit={handleSubmit} className="space-y-6">
                {step === 1 && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-[#0A2342] mb-3">Where do you want to live?</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {AREAS.map((area) => (
                          <button key={area.name} type="button" onClick={() => { setForm({...form, area: area.name}); setStep(2); }}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${form.area === area.name ? "border-[#DE6E4B] bg-[#DE6E4B]/5" : "border-slate-200 hover:border-[#8A9A5B]"}`}
                            data-testid={`seo-area-${area.name.toLowerCase().replace(/\s/g,'-')}`}
                          >
                            <MapPin className="w-5 h-5 text-[#DE6E4B] mb-1" />
                            <p className="font-bold text-[#0A2342]">{area.name}</p>
                            <p className="text-xs text-slate-500">{area.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-2 mb-2">
                      <button type="button" onClick={() => setStep(1)} className="text-sm text-[#DE6E4B] hover:underline">&larr; Back</button>
                      <span className="text-sm text-slate-500">Searching in <strong className="text-[#0A2342]">{form.area}</strong></span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Budget</label>
                        <select value={form.budget} onChange={(e) => setForm({...form, budget: e.target.value})} className="input-field" required data-testid="seo-budget">
                          <option value="">Select budget...</option>
                          {PRICE_RANGES.map(p => <option key={p} value={p}>{p}/month</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Bedrooms</label>
                        <select value={form.bedrooms} onChange={(e) => setForm({...form, bedrooms: e.target.value})} className="input-field" required data-testid="seo-bedrooms">
                          <option value="">Select...</option>
                          <option value="Studio">Studio</option>
                          <option value="1 Bed">1 Bedroom</option>
                          <option value="2 Bed">2 Bedrooms</option>
                          <option value="3+ Bed">3+ Bedrooms</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Your Name</label>
                        <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-field" placeholder="First & Last Name" required data-testid="seo-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                        <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="input-field" placeholder="your@email.com" required data-testid="seo-email" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone (optional)</label>
                      <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="input-field" placeholder="(555) 123-4567" data-testid="seo-phone" />
                    </div>
                    <button type="submit" disabled={loading} className="w-full py-4 bg-[#DE6E4B] text-white font-bold rounded-xl hover:bg-[#c5593d] transition-colors text-lg flex items-center justify-center" data-testid="seo-submit">
                      {loading ? <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" /> : <><Search className="w-5 h-5 mr-2" /> Find My Apartments</>}
                    </button>
                    <p className="text-xs text-slate-400 text-center">
                      <Shield className="w-3 h-3 inline mr-1" /> Your info is secure. We never share your data.
                    </p>
                  </div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popular Searches (SEO Content) */}
      <section className="py-20 bg-[#E8E8E8]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A2342] mb-4 text-center font-['Playfair_Display']">
            Popular Houston Apartment Searches
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Thousands of renters search for apartments in Houston every month. Here are the most popular searches we help with:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { title: "Apartments in Houston Under $1500", area: "Houston", budget: "$1,000 - $1,500" },
              { title: "Katy TX Apartments", area: "Katy", budget: "$1,000 - $1,500" },
              { title: "Apartments Near The Woodlands", area: "The Woodlands", budget: "$1,500 - $2,000" },
              { title: "Sugar Land TX Apartments", area: "Sugar Land", budget: "$1,500 - $2,000" },
              { title: "Cheap Apartments Houston", area: "Houston", budget: "Under $1,000" },
              { title: "Luxury Apartments Houston", area: "Houston", budget: "$2,500+" },
              { title: "Pet Friendly Apartments Houston", area: "Houston", budget: "$1,000 - $1,500" },
              { title: "Apartments With Move-In Specials", area: "Houston", budget: "$1,000 - $1,500" },
              { title: "Lake Conroe Area Apartments", area: "Conroe", budget: "Under $1,000" },
            ].map((item, i) => (
              <motion.a
                key={item.title}
                href="#finder"
                onClick={() => { setForm(f => ({...f, area: item.area, budget: item.budget})); setStep(2); }}
                initial={{opacity:0,y:20}}
                whileInView={{opacity:1,y:0}}
                transition={{delay:i*0.05}}
                className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg hover:border-[#DE6E4B] transition-all group cursor-pointer"
                data-testid={`seo-search-${i}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-[#0A2342] group-hover:text-[#DE6E4B] transition-colors">{item.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      <MapPin className="w-3 h-3 inline mr-1" />{item.area} &bull; {item.budget}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#DE6E4B] transition-colors" />
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Why Free */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A2342] mb-12 text-center font-['Playfair_Display']">
            Why Is My Service Free?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "You Search", desc: "Tell me your budget, preferred area, and move-in date. I'll pull a personalized apartment list." },
              { step: "2", title: "I Negotiate", desc: "I contact properties directly to get you the best move-in specials, waived fees, and exclusive deals." },
              { step: "3", title: "You Save", desc: "The apartment community pays my fee — not you. You get expert help and save hundreds on move-in." },
            ].map((item, i) => (
              <motion.div key={item.step} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} transition={{delay:i*0.1}} className="text-center">
                <div className="w-14 h-14 bg-[#DE6E4B] text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-[#0A2342] mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <a href="/book" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-[#0A2342] text-white font-bold rounded-xl hover:bg-[#0d2d54] transition-colors text-lg" data-testid="seo-bottom-cta"
            >
              <Calendar className="w-5 h-5 mr-2" /> Schedule My Free Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
